import { Archive, Lock, AlertTriangle } from 'lucide-react';

import type { ArchiveIssueView, ArchiveReportView } from '@/services/ingestion';

/**
 * What a ZIP in a package gave up — and what it did not.
 *
 * The line is written by the engine (`ArchiveReport.summary`), so the screen and
 * the record can never disagree: "ZIP: 14 files processed, 2 skipped (unsafe
 * path ×2), 1 encrypted — needs password". It is shown green only when every
 * entry that mattered was read; anything a person must look at (an encrypted
 * entry, an unreadable one, a skipped one, a read that stopped early) is listed
 * under it with its reason, never summarised away.
 */

const MAX_LISTED = 8;

const TONE: Record<ArchiveReportView['status'], string> = {
  ok: 'text-emerald-300',
  partial: 'text-amber-300',
  refused: 'text-red-300',
};

/** Skips that are only bookkeeping (identical content, an empty file) are not findings. */
const INFORMATIONAL = new Set(['duplicate', 'empty_file']);

interface Finding {
  issue: ArchiveIssueView;
  kind: 'encrypted' | 'failed' | 'skipped';
}

export function archiveFindings(archive: ArchiveReportView): Finding[] {
  return [
    ...(archive.encrypted ?? []).map((issue) => ({ issue, kind: 'encrypted' as const })),
    ...(archive.failed ?? []).map((issue) => ({ issue, kind: 'failed' as const })),
    ...(archive.skipped ?? [])
      .filter((issue) => !INFORMATIONAL.has(issue.code))
      .map((issue) => ({ issue, kind: 'skipped' as const })),
  ];
}

export default function ArchiveOutcome({ archive }: { archive: ArchiveReportView }) {
  const findings = archiveFindings(archive);
  const shown = findings.slice(0, MAX_LISTED);
  return (
    <div
      className="mt-1.5 space-y-0.5 rounded border border-stone-800 bg-black/30 px-2 py-1.5"
      data-testid="archive-outcome"
    >
      <p className={`flex items-start gap-1 text-[10px] ${TONE[archive.status] ?? 'text-stone-300'}`}>
        <Archive size={9} className="mt-[2px] shrink-0" />
        <span data-testid="archive-summary">{archive.summary}</span>
      </p>
      {shown.map(({ issue, kind }, i) => (
        <p
          key={`${kind}-${issue.path}-${i}`}
          className={`flex items-start gap-1 text-[10px] ${kind === 'failed' ? 'text-red-300' : 'text-amber-300/90'}`}
          title={`${issue.path} — ${issue.reason}`}
        >
          {kind === 'encrypted' ? (
            <Lock size={9} className="mt-[2px] shrink-0" />
          ) : (
            <AlertTriangle size={9} className="mt-[2px] shrink-0" />
          )}
          <span className="min-w-0 break-words">
            <span className="font-mono text-stone-300">{issue.path}</span>
            <span className="text-stone-500"> — {issue.reason}</span>
          </span>
        </p>
      ))}
      {findings.length > shown.length && (
        <p className="text-[10px] text-stone-500">+ {findings.length - shown.length} more — see the record's parse report</p>
      )}
      {archive.truncated && archive.truncatedReason && (
        <p className="text-[10px] text-amber-300/90">
          Read stopped early ({archive.truncatedReason}) — files after that point were not read.
        </p>
      )}
    </div>
  );
}

/** Every archive in a record's package, headed, for the record's own view. */
export function ArchiveOutcomeList({ archives }: { archives: ArchiveReportView[] | undefined }) {
  if (!archives?.length) return null;
  return (
    <div className="space-y-1" data-testid="archive-outcome-list">
      {archives.map((archive) => (
        <div key={archive.archive}>
          <p className="truncate font-mono text-[10px] text-stone-500" title={archive.archive}>
            {archive.archive}
          </p>
          <ArchiveOutcome archive={archive} />
        </div>
      ))}
    </div>
  );
}
